/**
 * Componente de Recibos - Versión Refactorizada
 * 
 * Mejoras implementadas:
 * - ✅ Botones estandarizados con ActionButton
 * - ✅ Código más limpio y organizado
 * - ✅ Separación de lógica en hooks personalizados
 * - ✅ Preparado para multi-tenant
 */

import React, { useState, useMemo } from 'react';
import { Table, Modal, Form, Card, Row, Col, InputGroup } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ActionButton, { ActionButtonGroup } from '../components/common/ActionButton';
import FirmaPad from '../components/FirmasPad';
import DescargaRecibo from '../components/Descrecibos';
import ReclamoForm from '../components/reclamoRecibo';
import ResolucionModal from '../components/ResolucionModal';
import ConfirmModal from '../components/ConfirmModal';
import '../styles/recibos.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://colaboradores.sibus.com.ar/';

const Recibos = ({ recibos, userLegajo, userName, refetchRecibos }) => {
  // ========================================
  // ESTADO
  // ========================================
  
  // Firma
  const [mostrarFirma, setMostrarFirma] = useState(false);
  const [reciboParaFirma, setReciboParaFirma] = useState(null);
  const [datosReciboActual, setDatosReciboActual] = useState(null);
  
  // Reclamo
  const [reciboParaReclamo, setReciboParaReclamo] = useState(null);
  
  // Resolución
  const [mostrarResolucion, setMostrarResolucion] = useState(false);
  const [resolucionActual, setResolucionActual] = useState(null);
  
  // Confirmación de validación
  const [showConfirm, setShowConfirm] = useState(false);
  const [reciboParaValidar, setReciboParaValidar] = useState(null);
  
  // Filtros
  const [filtroMes, setFiltroMes] = useState('');

  // ========================================
  // HELPERS
  // ========================================
  
  const parseMes = (mesStr) => {
    const [mes, anio] = mesStr.split('/').map(Number);
    return { mes, anio };
  };

  // ========================================
  // LÓGICA DE FIRMA
  // ========================================
  
  const handleSeleccionRecibo = async (recibo) => {
    const [mes, anio] = recibo.Mes.split('/');
    const legajoFormateado = userLegajo.toString().padStart(4, '0');
    const secuencia = recibo.Secuencia;
    const fileBase = `${anio}${mes}${secuencia}O${legajoFormateado}`;
    
    // ⚠️ IMPORTANTE: Codificar el mes porque contiene "/" que rompe la URL
    // Formato: "02/2026" → "02%2F2026" para que no se interprete como segmentos separados
    const mesEncoded = encodeURIComponent(recibo.Mes);
    const pdfPath = `${API_URL}/api/recibos/${mesEncoded}/${secuencia}/${userLegajo}/pdf`;
    const fileName = `${fileBase}.png`;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(pdfPath, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) {
        toast.error('📄 El archivo PDF del recibo no está disponible', {
          position: 'top-center',
          autoClose: 3000
        });
        return;
      }
      
      setReciboParaFirma(recibo);
      setDatosReciboActual({ fileName, pdfPath });
      setMostrarFirma(true);
    } catch (error) {
      console.error('Error verificando PDF:', error);
      toast.error('⚠️ No se pudo verificar la existencia del archivo PDF', {
        position: 'top-center',
        autoClose: 3000
      });
    }
  };

  const handleClickFirmarRecibo = async (recibo) => {
    return handleSeleccionRecibo(recibo);
  };

  const handleFirmaConfirmada = async (firmaDataUrl) => {
    if (!datosReciboActual || !reciboParaFirma) return;
    
    const base64 = firmaDataUrl.split(',')[1];
    const token = localStorage.getItem('token');

    const payload = {
      Mes: reciboParaFirma.Mes,
      Secuencia: reciboParaFirma.Secuencia,
      Legajo: userLegajo,
      Base64Image: base64,
    };

    try {
      // ✅ SOLUCIÓN PARA iPhone: Abrir ventana ANTES de las operaciones async
      const pdfUrl = datosReciboActual.pdfPath;
      const newWindow = window.open('', '_blank');
      
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 100vh;
                  margin: 0;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }
                .loader {
                  text-align: center;
                  color: white;
                }
                .spinner {
                  border: 4px solid rgba(255, 255, 255, 0.3);
                  border-radius: 50%;
                  border-top: 4px solid white;
                  width: 60px;
                  height: 60px;
                  animation: spin 1s linear infinite;
                  margin: 0 auto 20px;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                h2 {
                  font-size: 24px;
                  font-weight: 300;
                  margin: 0;
                }
              </style>
            </head>
            <body>
              <div class="loader">
                <div class="spinner"></div>
                <h2>✍️ Procesando firma...</h2>
                <p style="opacity: 0.8; font-size: 14px; margin-top: 10px;">Por favor espere</p>
              </div>
            </body>
          </html>
        `);
      }
      
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4005').replace(/\/$/, '');
      const response = await fetch(`${API_URL}/api/procesos/firmar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (newWindow) newWindow.close();
        toast.error('❌ Error al procesar la firma', {
          position: 'top-center',
          autoClose: 3000
        });
        throw new Error('Error al subir la firma');
      }

      localStorage.setItem('firmaEmpleado', base64);

      setMostrarFirma(false);
      setReciboParaFirma(null);
      setDatosReciboActual(null);

      await refetchRecibos();
      
      // Descargar el PDF con el token y crear un Object URL
      try {
        const pdfResponse = await fetch(pdfUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Redirigir la ventana ya abierta al PDF
          if (newWindow && !newWindow.closed) {
            newWindow.location.href = blobUrl;
          } else {
            // Fallback si la ventana fue bloqueada o cerrada
            window.open(blobUrl, '_blank');
          }
          
          // Liberar el objeto URL después de un tiempo
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
        } else {
          if (newWindow) newWindow.close();
          toast.error('❌ Error al descargar el PDF', {
            position: 'top-center',
            autoClose: 3000
          });
        }
      } catch (pdfError) {
        console.error('Error descargando PDF:', pdfError);
        if (newWindow) newWindow.close();
        toast.error('❌ Error al abrir el recibo', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error al firmar:', error);
      toast.error('❌ No se pudo enviar la firma', {
        position: 'top-center',
        autoClose: 3000
      });
    }
  };

  // ========================================
  // LÓGICA DE VALIDACIÓN
  // ========================================
  
  const handleConfirmValidation = async () => {
    if (!reciboParaValidar) return;
    
    try {
      const token = localStorage.getItem('token');
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4005').replace(/\/$/, '');

      // Validar recibo - Backend NUEVO migrado
      const validationResponse = await fetch(`${API_URL}/api/procesos/validar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mes: reciboParaValidar.Mes,
          secuencia: reciboParaValidar.Secuencia,
          legajo: userLegajo,
        }),
      });

      if (!validationResponse.ok) {
        throw new Error('Error al validar el recibo');
      }

      // Limpiar protesta
      const protestaResponse = await fetch(`${API_URL}/api/procesos/protestar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mes: reciboParaValidar.Mes,
          secuencia: reciboParaValidar.Secuencia,
          Protesta: 0,
          ObsProt: reciboParaValidar.ObsProt || '',
        }),
      });

      if (!protestaResponse.ok) {
        throw new Error('Error al actualizar estado de protesta');
      }

      setShowConfirm(false);
      setReciboParaValidar(null);
      await refetchRecibos();
      toast.success('✅ Recibo validado correctamente', {
        position: 'top-center',
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error al validar:', error);
      toast.error('❌ Error al procesar la validación', {
        position: 'top-center',
        autoClose: 3000
      });
    }
  };

  // ========================================
  // LÓGICA DE RECLAMO
  // ========================================
  
  const handleReclamoSuccess = async () => {
    await refetchRecibos();
  };

  const handleVerResolucion = (recibo) => {
    setResolucionActual(recibo);
    setMostrarResolucion(true);
  };

  // ========================================
  // FILTRADO Y ORDENAMIENTO
  // ========================================
  
  const recibosOrdenados = useMemo(() => {
    const filtrados = recibos.filter((recibo) =>
      recibo.Mes.toLowerCase().includes(filtroMes.toLowerCase())
    );

    return [...filtrados].sort((a, b) => {
      const { mes: mesA, anio: anioA } = parseMes(a.Mes);
      const { mes: mesB, anio: anioB } = parseMes(b.Mes);

      if (anioA !== anioB) return anioB - anioA;
      if (mesA !== mesB) return mesB - mesA;
      return b.Secuencia - a.Secuencia;
    });
  }, [recibos, filtroMes]);

  const grupos = useMemo(() => {
    const map = new Map();
    for (const r of recibosOrdenados) {
      if (!map.has(r.Mes)) map.set(r.Mes, []);
      map.get(r.Mes).push(r);
    }
    return Array.from(map.entries()).map(([Mes, items]) => ({ Mes, recibos: items }));
  }, [recibosOrdenados]);

  const recibosConBloqueo = useMemo(() => {
    const resultado = [];
    
    for (let i = 0; i < grupos.length; i++) {
      const grupo = grupos[i];
      const recibosDelMes = grupo.recibos;
      let bloqueado = false;

      if (i < grupos.length - 1) {
        const grupoAnterior = grupos[i + 1];
        const todosValidados = grupoAnterior.recibos.every((r) => r.Valida === 1);
        if (!todosValidados) bloqueado = true;
      }

      for (const r of recibosDelMes) {
        resultado.push({ ...r, bloqueado });
      }
    }
    
    return resultado;
  }, [grupos]);

  // ========================================
  // RENDER BOTONES DE ACCIÓN
  // ========================================
  
  const renderAccionesRecibo = (recibo) => {
    // Recibo bloqueado por mes anterior
    if (recibo.bloqueado) {
      return (
        <span className="text-muted small">
          🔒 Debe validar el mes anterior
        </span>
      );
    }

    // Recibo ya firmado
    if (recibo.Firmado === 1) {
      return (
        <ActionButtonGroup align="center">
          {/* Botón de descarga siempre disponible */}
          <DescargaRecibo recibo={recibo} userLegajo={userLegajo} />
          
          {/* Si no está validado, mostrar opciones */}
          {recibo.Valida !== 1 && (
            <>
              {/* Validar recibo */}
              <ActionButton
                variant="validate"
                tooltip="Validar recibo"
                onClick={() => {
                  setReciboParaValidar(recibo);
                  setShowConfirm(true);
                }}
              />

              {/* Realizar reclamo */}
              <ActionButton
                variant="warning"
                tooltip="Realizar reclamo"
                onClick={() => setReciboParaReclamo(recibo)}
              />

              {/* Ver respuesta de RRHH si existe */}
              {recibo.Resuelto === 1 && (
                <ActionButton
                  variant="info"
                  tooltip="Ver respuesta de RRHH"
                  onClick={() => handleVerResolucion(recibo)}
                />
              )}
            </>
          )}
        </ActionButtonGroup>
      );
    }

    // Recibo sin firmar
    return (
      <ActionButton
        variant="download"
        tooltip="Firmar y descargar recibo"
        onClick={() => handleClickFirmarRecibo(recibo)}
      >
        Firmar y descargar
      </ActionButton>
    );
  };

  // ========================================
  // RENDER PRINCIPAL
  // ========================================
  
  return (
    <div className="container py-4">
      <Card className="shadow-lg rounded-4 p-4">
        <h3 className="mb-4 text-center text-dark-emphasis">📄 Recibos de Sueldo</h3>

        {/* Barra de búsqueda */}
        <Row className="mb-4">
          <Col md={6} className="mx-auto">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por mes (ej: 04/2025)"
                value={filtroMes}
                onChange={(e) => setFiltroMes(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>

        {/* Tabla de recibos */}
        <div className="table-responsive">
          <Table hover bordered className="tabla-estilizada">
            <thead className="table-encabezado text-white">
              <tr>
                <th>#</th>
                <th>Nº Secuencia</th>
                <th>Período</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {recibosConBloqueo.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">
                    No se encontraron recibos
                  </td>
                </tr>
              ) : (
                recibosConBloqueo.map((recibo, index) => (
                  <tr
                    key={`${recibo.Mes}-${recibo.Secuencia}`}
                    className={index % 2 === 0 ? 'fila-celeste' : 'fila-blanca'}
                  >
                    <td>{index + 1}</td>
                    <td>{recibo.Secuencia}</td>
                    <td>{recibo.Mes}</td>
                    <td className="text-center">
                      {renderAccionesRecibo(recibo)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Modal de firma */}
      <Modal show={mostrarFirma} onHide={() => setMostrarFirma(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Firma del Recibo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {datosReciboActual && (
            <FirmaPad
              onFirmar={handleFirmaConfirmada}
              onCancelar={() => setMostrarFirma(false)}
              fileName={datosReciboActual.fileName}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de reclamo */}
      {reciboParaReclamo && (
        <ReclamoForm
          show={!!reciboParaReclamo}
          onHide={() => setReciboParaReclamo(null)}
          recibo={reciboParaReclamo}
          userLegajo={userLegajo}
          userName={userName}
          onReclamoSuccess={handleReclamoSuccess}
        />
      )}

      {/* Modal de resolución */}
      <ResolucionModal
        show={mostrarResolucion}
        onHide={() => setMostrarResolucion(false)}
        resolucion={resolucionActual}
      />

      {/* Modal de confirmación de validación */}
      {showConfirm && (
        <ConfirmModal
          onConfirm={handleConfirmValidation}
          onCancel={() => {
            setShowConfirm(false);
            setReciboParaValidar(null);
          }}
        />
      )}
    </div>
  );
};

export default Recibos;
